import React, { useState } from "react";
import {
  Container,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  FormHelperText,
  Box,
  IconButton,
} from "@mui/material";
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { Helmet } from "react-helmet";
import { API_ENDPOINT } from "../config";

const botEndpoints = {
  advisor: {
    set: API_ENDPOINT + "drinvestor/set",
    remove: API_ENDPOINT + "drinvestor/remove",
    hint: API_ENDPOINT + "drinvestor/received",
    helptext: "Share market advisor for stock purchase and forecasting.",
  },
  butler: {
    set: API_ENDPOINT + "set0",
    remove: API_ENDPOINT + "remove0",
    hint: API_ENDPOINT + "maps",
    helptext: "Butler is a genral purpose bot.",
  },
  spax_service: {
    set: API_ENDPOINT + "set1",
    remove: API_ENDPOINT + "remove1",
    hint: API_ENDPOINT + "maps",
    helptext: "spax services is a single bot to search information on sixpetal network.",
  },
  prashant: {
    set: API_ENDPOINT + "chatbot/set",
    remove: API_ENDPOINT + "chatbot/remove",
    hint: API_ENDPOINT + "chatbot/received",
    helptext: "LLM based chatbot from my resume.",
  },
};

const SetHook = () => {
  const [botID, setBotID] = useState("");
  const [receiver, setReceiver] = useState("");
  const [helptext, setHelptext] = useState("");
  const [result, setResult] = useState("");
  const navigate = useNavigate();

  const handleBotChange = (event) => {
    const selected = event.target.value;
    setBotID(selected);

    if (selected && botEndpoints[selected]) {
      setReceiver(botEndpoints[selected].hint);
      setHelptext(botEndpoints[selected].helptext);
    } else {
      setReceiver("");
      setHelptext("");
    }
  };

  const setWebhook = async () => {
    if (!botID || !botEndpoints[botID]) {
      setResult("Please select a valid Bot ID");
      return;
    }

    try {
      const response = await fetch(
        `${botEndpoints[botID].set}?url=${encodeURIComponent(receiver)}`
      );
      const data = await response.json();
      setResult(JSON.stringify(data, null, 2));
    } catch (error) {
      setResult("Error: " + JSON.stringify(error));
    }
  };

  const removeWebhook = async () => {
    if (!botID || !botEndpoints[botID]) {
      setResult("Please select a valid Bot ID");
      return;
    }

    try {
      const response = await fetch(botEndpoints[botID].remove);
      const data = await response.json();
      setResult(JSON.stringify(data, null, 2));
    } catch (error) {
      setResult("Error: " + JSON.stringify(error));
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 5 }}>
      <Helmet>
        <title>Bot Management Dashboard | Redmorus</title>
        <meta name="description" content="Easily manage, monitor, and configure bots in your infrastructure with RedMorus. Track behavior, define access rules, and automate maintenance tasks." />
      </Helmet>

      <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
        <motion.div
          whileHover={{ x: -5 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          <IconButton onClick={() => navigate('/')} color="primary">
            <ArrowBackIcon />
          </IconButton>
        </motion.div>
        <Typography variant="h6" sx={{ ml: 1 }}>
          Back
        </Typography>
      </Box>
      <Box display="flex" alignItems="center">
        <Box
          component="img"
          src="/logo64.png"
          alt="Redmorus Logo"
          sx={{ width: 20, height: 20, mr: 1 }}
        />
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          Redmorus
        </Typography>
      </Box>
      <Typography variant="h4" gutterBottom>
        Telegram Bot Control Panel
      </Typography>

      <FormControl fullWidth margin="normal">
        <InputLabel id="bot-id-label">Bot ID</InputLabel>
        <Select
          labelId="bot-id-label"
          value={botID}
          label="Bot ID"
          onChange={handleBotChange}
        >
          <MenuItem value="">Select Bot ID</MenuItem>
          <MenuItem value="advisor">Advisor</MenuItem>
          <MenuItem value="butler">Butler</MenuItem>
          <MenuItem value="spax_service">Spax Service</MenuItem>
          <MenuItem value="prashant">Prashant</MenuItem>
        </Select>
        <FormHelperText>{helptext}</FormHelperText>
      </FormControl>

      <TextField
        fullWidth
        margin="normal"
        label="Receiver"
        value={receiver}
        onChange={(e) => setReceiver(e.target.value)}
      />

      <Box mt={2} display="flex" gap={2}>
        <Button variant="contained" color="primary" onClick={setWebhook}>
          Set WebHook
        </Button>
        <Button variant="contained" color="error" onClick={removeWebhook}>
          Remove WebHook
        </Button>
      </Box>

      <Box mt={4}>
        <Typography variant="subtitle2">Result</Typography>
        <pre style={{ backgroundColor: "#f4f4f4", padding: "1rem" }}>
          {result}
        </pre>
      </Box>
    </Container>
  );
};

export default SetHook;
